<script lang="ts" setup>
// @ts-expect-error ?skipsvgo is not typed
import Location from "assets/icons/location.svg?skipsvgo";
import {useDebounceFn} from "@vueuse/core";

const REGION = "NL";
const AUTOCOMPLETE_DELAY = 1000;

const {t} = useI18n();
const {maps} = useGoogleMapsApi();
const emit = defineEmits<{
  autocomplete: [place: google.maps.GeocoderResult | undefined];
}>();
const loading = ref(false);
const suggestionList = ref<any[]>([]);
const showSuggestions = ref<boolean>(false);
const query = ref<string>("");

const onQueryInput = useDebounceFn((event: Event) => {
  search((event.target as HTMLInputElement).value);
}, AUTOCOMPLETE_DELAY);

async function search(q: string) {
  if (maps.value && q.length > 0) {
    loading.value = true;
    userGeoHandlingDone.value = true;
    const {suggestions} =
      await maps.value.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
        {
          input: q,
          region: REGION,
        },
      );
    loading.value = false;
    suggestionList.value = suggestions.flatMap(({placePrediction}) =>
      placePrediction
        ? [{label: placePrediction.text.text, placeId: placePrediction.placeId}]
        : [],
    );
    showSuggestions.value = true;
  }
}

const userGeoResults = ref<google.maps.GeocoderResult[]>([]);
const searchInputPlaceholder = computed(() => {
  const [location] = userGeoResults.value;
  return location
    ? location.formatted_address
    : t("label.autocompletePlaceholder");
});
async function reverseGeocode(location: google.maps.LatLngLiteral) {
  if (maps.value && location.lat < Infinity) {
    const geocoder = new maps.value.Geocoder();
    const response = await geocoder.geocode({
      location,
      fulfillOnZeroResults: true,
    });
    userGeoResults.value = response.results;
    query.value = "";
    const [item] = response.results;
    if (item) {
      emit("autocomplete", item);
    }
  }
}

async function onSelectSuggestion(placeId: string) {
  if (maps.value) {
    const geocoder = new maps.value.Geocoder();
    const response = await geocoder.geocode({
      placeId,
      fulfillOnZeroResults: true,
    });
    const [item] = response.results;
    if (item) {
      emit("autocomplete", item);
      query.value = item.formatted_address;
    }
  }
  showSuggestions.value = false;
}

watch(maps, () => onLocation(), {
  once: true,
});

const userGeo = ref<google.maps.LatLngLiteral>({
  lat: Infinity,
  lng: Infinity,
});
const userGeoHandlingDone = ref<boolean>(false);
const userGeoHandlingBusy = ref<boolean>(false);

const onLocation = () => {
  if ("geolocation" in navigator) {
    if (userGeoHandlingBusy.value) return;
    userGeoHandlingBusy.value = true;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        userGeo.value.lat = position.coords.latitude;
        userGeo.value.lng = position.coords.longitude;
        reverseGeocode(userGeo.value);
        userGeoHandlingDone.value = true;
        userGeoHandlingBusy.value = false;
      },
      () => {
        userGeoHandlingDone.value = true;
        userGeoHandlingBusy.value = false;
      },
    );
  }
};
</script>

<template>
  <div class="w-full">
    <div class="flex gap-3 border border-white p-3">
      <input
        :value="query"
        @input="onQueryInput"
        :placeholder="searchInputPlaceholder"
        type="text"
        class="w-full bg-black border-0 outline-none focus:border-0 focus:ring-0 text-white" />
      <Location
        :fontControlled="false"
        class="w-6 h-6 cursor-pointer"
        :class="{'animate-spin': userGeoHandlingBusy}"
        @click="onLocation" />
    </div>
    <div class="relative" v-if="showSuggestions">
      <div
        class="absolute w-full z-10 flex flex-col bg-black border border-white top-2">
        <div
          tabindex="0"
          class="px-3 py-1 cursor-pointer hover:bg-neutral-800"
          v-for="item in suggestionList"
          @click="onSelectSuggestion(item.placeId)">
          {{ item.label }}
        </div>
      </div>
    </div>
  </div>
</template>
